// ============================================================================
// Wave 3.26 — Shared Order Comments context
//
// Centralizes the order-comment state so multiple sections of the Order
// Detail page (Comments panel, Ghat header, Requirement rows, Raw Material
// Ledger material headers + packet groups) can:
//
//  1. Read a single source of truth `comments` array
//  2. Reload from the API after a mutation
//  3. Filter comments by topic / subtopic / sub-subtopic
//  4. Toggle an "active filter" that drives an inline filtered display
//     under the section header whose badge was clicked
// ============================================================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';

const OrderCommentsContext = createContext(null);

const isMaterialSlug = (s) => ['gold', 'diamond', 'color_stone', 'pearl'].includes(s);

/**
 * filterCommentsBy(spec)
 *
 * spec shape:
 *   { topic: 'ghat' | 'requirement' | 'raw_material' | 'general',
 *     subtopic_id?: string,          // requirement.id or material slug
 *     sub_subtopic_id?: string,      // packet_id (for raw_material)
 *     packetMatch?: boolean,         // when true, match exactly this packet
 *     materialMatch?: boolean,       // when true, match all comments under this material category
 *     includePacketComments?: boolean // when matching by material, also include comments tagged to packets in that material
 *   }
 */
export function matchComment(c, spec) {
  if (!spec) return true;
  const cTopic = (c.topic || 'general').toLowerCase();
  const want = (spec.topic || '').toLowerCase();

  if (want && cTopic !== want) return false;

  if (want === 'requirement') {
    if (spec.subtopic_id && c.subtopic_id !== spec.subtopic_id) return false;
    return true;
  }

  if (want === 'raw_material') {
    // Material-level match: include all comments under that material category,
    // optionally including packet-level (sub-sub) comments.
    if (spec.subtopic_id) {
      if (c.subtopic_id !== spec.subtopic_id) return false;
    }
    if (spec.sub_subtopic_id) {
      if (c.sub_subtopic_id !== spec.sub_subtopic_id) return false;
    }
    return true;
  }

  if (want === 'ghat') {
    // Wave 3.65 — Ghat comments may be scoped to a milestone via
    // subtopic_id ∈ overall|cad|cam|ghat. Callers that omit
    // ``subtopic_id`` (e.g. the section-level "Ghat" badge) still
    // match every ghat comment; callers that pass a specific
    // milestone (`cad` etc.) narrow the match down.
    if (spec.subtopic_id) {
      const cSub = (c.subtopic_id || '').toLowerCase();
      if (cSub !== spec.subtopic_id) return false;
    }
    return true;
  }

  if (want === 'setting' || want === 'qc') {
    // Setting → subtopic_id ∈ diamond|color_stone|pearl (raw material head).
    // QC      → subtopic_id ∈ gold_testing|cvd_testing (test head).
    // Callers omitting subtopic_id (e.g. an overall QC badge) match every
    // comment on that topic; a specific head narrows the match.
    if (spec.subtopic_id) {
      const cSub = (c.subtopic_id || '').toLowerCase();
      if (cSub !== spec.subtopic_id) return false;
    }
    return true;
  }

  // general — topic match already enforced above.
  return true;
}

export function OrderCommentsProvider({ orderId, children }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  // Wave 3.68 — enforce SINGLE-OPEN behaviour: only one comment callout
  // is ever open at a time. Opening a different badge closes any
  // previously open one; clicking the same badge closes it. This
  // replaces the earlier multi-open map semantics.
  const [openFilters, setOpenFilters] = useState({}); // { [key: string]: filterSpec } — always 0 or 1 keys.

  // Wave 3.68 — Composer preset lets row-level "Add Comment" shortcuts
  // hand the composer a pre-scoped topic/subtopic. The main
  // OrderCommentsSection subscribes to this token, applies the topic
  // + subtopic to its local state, scrolls to itself, and focuses the
  // textarea. `token` is a bumped counter so identical presets still
  // re-trigger the effect.
  const [composerPreset, setComposerPreset] = useState(null); // { token, topic, subtopicId, subSubtopicId }

  const reload = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}/comments`);
      setComments(Array.isArray(data) ? data : []);
    } catch (e) {
      // 404 = order missing; ignore silently
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const countFor = useCallback(
    (spec) => comments.filter((c) => matchComment(c, spec)).length,
    [comments],
  );

  const filter = useCallback(
    (spec) => comments.filter((c) => matchComment(c, spec)),
    [comments],
  );

  // Wave 3.68 — Single-open semantics:
  //   - clicking the same key that's already open → close it (empty map)
  //   - clicking a different key → replace with { [key]: spec }
  const toggleFilter = useCallback((key, spec) => {
    setOpenFilters((prev) => {
      if (prev[key]) {
        return {};
      }
      return { [key]: spec };
    });
  }, []);

  const closeFilter = useCallback((key) => {
    setOpenFilters((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const isOpen = useCallback((key) => !!openFilters[key], [openFilters]);

  // Wave 3.68 — Preset the composer scope. Bumps the token so consumers
  // re-run their effect even when the same preset is requested twice.
  // ``opts.scroll`` (default true) controls whether the composer scrolls into
  // view + focuses — set false for a silent pre-scope (e.g. when a checklist
  // callout is merely opened, so we don't yank the page away from the list).
  const applyComposerPreset = useCallback((spec, opts = {}) => {
    setComposerPreset((prev) => ({
      token: (prev?.token || 0) + 1,
      topic: spec?.topic || 'general',
      subtopicId: spec?.subtopicId || '',
      subSubtopicId: spec?.subSubtopicId || '',
      scroll: opts.scroll !== false,
    }));
  }, []);

  const value = useMemo(
    () => ({
      comments,
      loading,
      reload,
      countFor,
      filter,
      toggleFilter,
      isOpen,
      closeFilter,
      composerPreset,
      applyComposerPreset,
    }),
    [
      comments, loading, reload, countFor, filter,
      toggleFilter, isOpen, closeFilter,
      composerPreset, applyComposerPreset,
    ],
  );

  return (
    <OrderCommentsContext.Provider value={value}>
      {children}
    </OrderCommentsContext.Provider>
  );
}

export function useOrderComments() {
  const ctx = useContext(OrderCommentsContext);
  if (!ctx) {
    // Allow components to opt-out gracefully when used outside a provider.
    return {
      comments: [],
      loading: false,
      reload: async () => {},
      countFor: () => 0,
      filter: () => [],
      toggleFilter: () => {},
      isOpen: () => false,
      closeFilter: () => {},
      composerPreset: null,
      applyComposerPreset: () => {},
    };
  }
  return ctx;
}

export { isMaterialSlug };
